import { FC, useEffect, useState } from 'react'
import classNames from 'classnames'
import { createPortal } from 'react-dom'
import { ReactComponent as MetaLogo } from 'src/assets/svg/meta-ads.svg'
import { useAdPerfAdDetail } from 'src/reactQuery/useAdPerformance'
import {
  AdPerfAdDetail,
  AdPerfRange,
  AdPerfSplit,
} from 'src/services/adPerformanceApi'
import s from './dashboard.module.scss'
import { LinkIcon } from './icons'
import {
  compact,
  genderLabel,
  money,
  objectiveLabel,
  pct,
  placementLabel,
} from '../format'

interface Props {
  // Shown as the post author in the preview (the ad account's name).
  accountName?: string
  adId: string | null
  currency: string
  onClose: () => void
  range: AdPerfRange
}

const Split: FC<{ label: (v: string) => string; rows: AdPerfSplit[] }> = ({
  label,
  rows,
}) => (
  <>
    {rows
      .filter((r) => r.share)
      .map((r) => (
        <div className={s.splitRow} key={r.value}>
          <span>{label(r.value)}</span>
          <div className={s.splitTrack}>
            <span style={{ width: `${r.share}%` }} />
          </div>
          <span className={s.splitPct}>{r.share}%</span>
        </div>
      ))}
  </>
)

const CTA_LABEL = (cta: string) =>
  cta
    .toLowerCase()
    .split('_')
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(' ')

const PostPreview: FC<{ accountName: string; data: AdPerfAdDetail }> = ({
  accountName,
  data,
}) => {
  const [more, setMore] = useState(false)
  const long = (data.body?.length ?? 0) > 160
  return (
    <div className={s.post}>
      <div className={s.postHead}>
        <span className={s.postAvatar}>{accountName.trim()[0]}</span>
        <div>
          <strong>{accountName}</strong>
          <small>Sponsored</small>
        </div>
      </div>
      {data.body && (
        <div className={classNames(s.postBody, { [s.postBodyClamp]: !more })}>
          {data.body}
        </div>
      )}
      {long && (
        <button
          className={s.postMore}
          onClick={() => setMore((v) => !v)}
          type="button"
        >
          {more ? 'See less' : 'See more'}
        </button>
      )}
      {data.thumbnail_url && (
        <img alt="" className={s.postImage} src={data.thumbnail_url} />
      )}
      {(data.title || data.cta) && (
        <div className={s.postCta}>
          {data.title && <div className={s.postTitle}>{data.title}</div>}
          {data.cta && data.cta !== 'NO_BUTTON' && (
            <span className={s.postCtaButton}>{CTA_LABEL(data.cta)}</span>
          )}
        </div>
      )}
      <div className={s.postActions}>
        <span>Like</span>
        <span>Comment</span>
        <span>Share</span>
      </div>
    </div>
  )
}

export const AdDetailModal: FC<Props> = ({
  accountName = 'Ad account',
  adId,
  currency,
  onClose,
  range,
}) => {
  const { data, isLoading } = useAdPerfAdDetail(range, adId)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  if (!adId) return null
  const t = data?.totals
  const postUrl =
    data?.instagram_url ||
    (data?.post_id ? `https://www.facebook.com/${data.post_id}` : null) ||
    data?.preview_url

  return createPortal(
    <div className={s.backdrop} onClick={onClose} role="presentation">
      <div
        className={s.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
      >
        <button
          aria-label="Close"
          className={s.close}
          onClick={onClose}
          type="button"
        >
          ×
        </button>
        <h2 className={s.modalTitle}>
          Campaigns › {data?.campaign?.name ?? (isLoading ? '…' : '')}
        </h2>
        {data && t && (
          <div className={s.detailGrid}>
            <div className={s.card}>
              <h3 className={s.cardTitle}>{data.name}</h3>
              <div className={s.pills}>
                {data.status && (
                  <span
                    className={classNames(s.pill, {
                      [s.pillGreen]: data.status === 'ACTIVE',
                    })}
                  >
                    {data.status === 'ACTIVE' && <i className={s.dot} />}
                    {data.status[0] + data.status.slice(1).toLowerCase()}
                  </span>
                )}
                <span className={classNames(s.pill, s.pillBlue)}>
                  <MetaLogo className={s.pillLogo} /> Meta
                </span>
                <span className={classNames(s.pill, s.pillGreen)}>
                  {objectiveLabel(data.objective)}
                </span>
              </div>
              <div className={s.sectionLabel}>Performance</div>
              <div className={s.statGrid}>
                <div className={s.stat}>
                  Impressions<strong>{compact(t.impressions)}</strong>
                </div>
                <div
                  className={s.stat}
                  title="Highest single-day reach. Period reach is not the sum of days."
                >
                  Reach (est.)
                  <strong>
                    {data.reach_floor ? `≥ ${compact(data.reach_floor)}` : '–'}
                  </strong>
                </div>
                <div className={s.stat}>
                  Clicks<strong>{compact(t.clicks)}</strong>
                </div>
                <div className={s.stat}>
                  CTR<strong>{pct(t.ctr)}</strong>
                </div>
                <div className={s.stat}>
                  Spent<strong>{money(t.spend, currency, 2)}</strong>
                </div>
                <div className={s.stat}>
                  CPM<strong>{money(t.cpm, currency, 2)}</strong>
                </div>
              </div>
              <div className={s.divider} />
              <div className={s.sectionLabel}>Breakdown by impressions</div>
              <div className={s.splitTitle}>Gender Distribution</div>
              <Split label={genderLabel} rows={data.gender} />
              <div className={s.splitTitle}>Age Distribution</div>
              <Split
                label={(v) => v}
                rows={[...data.age].sort((a, b) =>
                  a.value < b.value ? -1 : 1
                )}
              />
              <div className={s.divider} />
              <div className={s.splitTitle}>Placement</div>
              <Split label={placementLabel} rows={data.placement.slice(0, 5)} />
            </div>
            <div className={s.card}>
              <h3 className={s.previewHeading}>Ad Preview</h3>
              <PostPreview accountName={accountName} data={data} />
              {postUrl && (
                <a
                  className={s.openPost}
                  href={postUrl}
                  rel="noreferrer"
                  target="_blank"
                >
                  <LinkIcon /> Open Original Post
                </a>
              )}
              <p className={s.previewMeta}>
                <span>
                  Format: {data.format[0].toUpperCase() + data.format.slice(1)}
                </span>
                {data.cta && data.cta !== 'NO_BUTTON' && (
                  <span>CTA: {CTA_LABEL(data.cta)}</span>
                )}
                {data.campaign && <span>Campaign: {data.campaign.name}</span>}
                {data.adset && <span>Ad set: {data.adset.name}</span>}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}
